import { Alert, Box, Collapse, Link, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

import { PreviewValidationResultsGrid } from './PreviewValidationResultsGrid';
import ValidationResultsGrid from './ValidationResultsGrid';
import ValidationResultsHeader from './ValidationResultsHeader';
import { useExportPreviewConcepts } from '../hooks/useExportPreviewConcepts';
import { useExportValidationResults } from '../hooks/useExportValidationResults';

interface ValidationResultsStepProps {
  conceptGroupName: string;
  concepts?: Array<IsValidCreateUploadResponse>;
  warnings?: string[] | null;
  previewId?: string;
  totalCount?: number;
  domainBreakdown?: Record<string, number>;
}

const ParseWarnings = ({ warnings }: { warnings: string[] }) => {
  const [expanded, setExpanded] = useState(false);

  const summaries = warnings.filter((w) => !w.startsWith('detail:'));
  const details = warnings
    .filter((w) => w.startsWith('detail:'))
    .map((w) => w.replace('detail:', ''));

  return (
    <Alert severity="warning">
      {summaries.map((summary, i) => (
        <Typography key={i} variant="body2">
          {summary}
        </Typography>
      ))}
      {details.length > 0 && (
        <>
          <Link
            component="button"
            variant="body2"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ mt: 0.5 }}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </Link>
          <Collapse in={expanded}>
            <Box
              component="pre"
              sx={{
                mt: 1,
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                m: 0,
                p: 0,
              }}
            >
              {details.join('\n')}
            </Box>
          </Collapse>
        </>
      )}
    </Alert>
  );
};

export const ValidationResultsStep = ({
  conceptGroupName,
  concepts,
  warnings,
  previewId,
  totalCount,
  domainBreakdown,
}: ValidationResultsStepProps) => {
  const legacyExport = useExportValidationResults({
    concepts: previewId ? undefined : concepts,
    conceptGroupName,
  });

  const previewExport = useExportPreviewConcepts({
    previewId: previewId || '',
    conceptGroupName,
    totalCount: totalCount || 0,
  });

  const { exporting, handleExportAll } = previewId ? previewExport : legacyExport;

  return (
    <Box width={'100%'}>
      <Stack direction="column" gap={4}>
        {warnings && warnings.length > 0 && <ParseWarnings warnings={warnings} />}
        <ValidationResultsHeader
          concepts={concepts}
          exporting={exporting}
          onExportAll={handleExportAll}
          totalCount={totalCount}
          domainBreakdown={domainBreakdown}
        />
        {previewId ? (
          <PreviewValidationResultsGrid previewId={previewId} />
        ) : (
          <ValidationResultsGrid concepts={concepts} />
        )}
      </Stack>
    </Box>
  );
};
